import { v4 as uuid } from 'uuid'
import { cloneDeep } from 'lodash-es'
import type { FilterStore } from '~/stores/types'
import type {
  AssetFilterGrouping,
  BasicFilters,
  FilterClause,
  FilterClausePayload,
  FilterGroup,
  FilterItem,
  LogicOperator,
  Operator,
  ShareLevel
} from '~/types/filters'
import { cleanEmptyClauses } from '~/utils/telemetries'
import type { AssetType } from '~/types/asset'
import { isRouteLevelProperty } from '~/utils/routes'

const ROUTE_FAMILY = 'routes'

const propertyFamily = (property: string): string => (isRouteLevelProperty(property) ? ROUTE_FAMILY : property)

export const useFilterStore = defineStore(
  'filterStore',
  (): FilterStore => {
    // State
    const id = ref<number>(0)
    const name = ref<string>('')
    const groupBy = ref<AssetFilterGrouping>('MODULE')
    const assetTypeIds = ref<string[]>([])
    const assetIds = ref<number[]>([])
    const shareLevel = ref<ShareLevel>('PRIVATE')
    const rootCondition = ref<FilterGroup>({
      _id: 'root',
      type: 'group',
      operator: '$and',
      children: []
    })
    const availableAssetTypes = ref<AssetType[]>([])

    // Actions
    const addClause = (groupId: string, clause: FilterClausePayload): void => {
      const root = cloneDeep(rootCondition.value)

      const group = _findGroupById(root, groupId)
      if (group) {
        const filterClause = {
          _id: uuid(),
          type: 'clause',
          ...clause
        } as FilterClause

        const existingClause = findClauseInGroup(group._id, filterClause)
        if (existingClause) {
          return
        }

        group.children.push(filterClause)
        rootCondition.value = root
      }
    }

    const addGroup = (groupId: string, operator: LogicOperator): void => {
      const root = cloneDeep(rootCondition.value)

      const parent = _findGroupById(root, groupId)

      if (parent) {
        parent.children.push({
          _id: uuid(),
          type: 'group',
          operator,
          children: []
        })

        rootCondition.value = root
      }
    }

    const updateClause = (clauseId: string, payload: FilterClausePayload): void => {
      rootCondition.value = _updateNestedClause(rootCondition.value, clauseId, payload) as FilterGroup
    }

    const removeItemById = (id: string): void => {
      rootCondition.value = _removeNestedItem(rootCondition.value, id) as FilterGroup
    }

    const fromBasicFilters = (filters: BasicFilters): void => {
      const groupsByFamily = new Map<string, FilterGroup>()

      for (const [property, values] of Object.entries(filters)) {
        if (!Array.isArray(values) || values.length === 0) {
          continue
        }

        const family = propertyFamily(property)
        const propertyGroup = groupsByFamily.get(family) ?? {
          _id: uuid(),
          type: 'group',
          operator: '$or',
          children: []
        }
        propertyGroup.children.push(
          ...values.map((value) => ({
            _id: uuid(),
            type: 'clause' as const,
            property,
            operator: '=' as const,
            value
          }))
        )
        groupsByFamily.set(family, propertyGroup)
      }

      const mainGroup = [...groupsByFamily.values()]

      rootCondition.value = {
        _id: 'root',
        type: 'group',
        operator: '$and',
        children: mainGroup
      }
    }

    const setId = (filterId: number): void => {
      id.value = filterId
    }

    const setName = (filterName: string): void => {
      name.value = filterName
    }

    const setGroupBy = (grouping: AssetFilterGrouping): void => {
      groupBy.value = grouping
    }

    const setAssetTypeIds = (ids: string[]): void => {
      assetTypeIds.value = ids
    }

    const setAssetIds = (ids: number[]): void => {
      assetIds.value = ids
    }

    const setShareLevel = (level: ShareLevel): void => {
      shareLevel.value = level
    }

    const setRootCondition = (value: FilterGroup): void => {
      rootCondition.value = value
    }

    const resetClauses = (): void => {
      rootCondition.value = {
        _id: 'root',
        type: 'group',
        operator: '$and',
        children: []
      }
    }

    const clearAllConditions = (): void => {
      assetTypeIds.value = _getAllAssetTypes()
      assetIds.value = []
      shareLevel.value = 'PRIVATE'
      groupBy.value = 'MODULE'
      rootCondition.value = {
        _id: 'root',
        type: 'group',
        operator: '$and',
        children: []
      }
    }

    const findClauseInGroup = (
      groupId: string,
      clause: Omit<FilterClause, '_id' | 'type'>
    ): FilterClause | undefined => {
      const group = _findGroupById(rootCondition.value, groupId)
      if (group) {
        return group.children.find((child) => {
          return (
            child.type === 'clause' &&
            child.property === clause.property &&
            child.operator === clause.operator &&
            child.value === clause.value
          )
        }) as FilterClause | undefined
      }
      return undefined
    }

    const findClausesByPropertyInGroup = (groupId: string, property: string, operator: Operator): FilterClause[] => {
      const group = _findGroupById(rootCondition.value, groupId)
      if (group) {
        return group.children.filter((child) => {
          return child.type === 'clause' && child.property === property && child.operator === operator
        }) as FilterClause[]
      }
      return []
    }

    const setAvailableAssetTypes = (types: AssetType[]): void => {
      if (availableAssetTypes.value.length === 0) {
        assetTypeIds.value = types.map((assetType) => assetType.assetTypeId)
      }
      availableAssetTypes.value = types
    }
    // Internal helpers

    const _findGroupById = (item: FilterItem, id: string): FilterGroup | undefined => {
      if (item.type === 'group') {
        if (item._id === id) {
          return item
        }

        for (const child of item.children) {
          const result = _findGroupById(child, id)
          if (result) {
            return result
          }
        }
      }

      return undefined
    }

    const _findClauseById = (item: FilterItem, id: string): FilterClause | undefined => {
      if (item.type === 'clause') {
        if (item._id === id) {
          return item
        }
      } else {
        for (const child of item.children) {
          const result = _findClauseById(child, id)
          if (result) {
            return result
          }
        }
      }

      return undefined
    }

    const _updateNestedClause = (item: FilterItem, id: string, payload: FilterClausePayload): FilterItem => {
      if (item.type === 'clause') {
        if (item._id === id) {
          return {
            ...item,
            ...payload
          }
        }
        return item
      }

      return {
        ...item,
        children: item.children.map((child) => _updateNestedClause(child, id, payload))
      }
    }

    const _removeNestedItem = (item: FilterItem, id: string): FilterItem | null => {
      if (item.type === 'clause') {
        return item._id === id ? null : item
      }

      const updatedChildren = item.children.map((child) => _removeNestedItem(child, id)).filter(Boolean) as FilterItem[]

      return {
        ...item,
        children: updatedChildren
      }
    }

    const _getAllAssetTypes = (): string[] => {
      return availableAssetTypes.value.map((assetType) => assetType.assetTypeId)
    }
    // Getters

    const filters = computed<FilterItem[]>(() => rootCondition.value.children)

    const conditionCount = computed<number>(() => {
      const countClauses = (filterItem: FilterItem): number => {
        if (filterItem.type === 'clause') {
          return 1
        } else {
          return (
            filterItem?.children.reduce((sum, child) => {
              return sum + countClauses(child)
            }, 0) || 0
          )
        }
      }

      const condition = cleanEmptyClauses(rootCondition.value)
      return countClauses(condition)
    })

    const hasValidFilters = computed(() => {
      return assetTypeIds.value.length > 0 // Todo: Review how we determine how filters are valid
    })

    // const hasFilters = computed(() => {
    //   const containsClause = (filterItem: FilterItem): boolean => {
    //     if (filterItem.type === 'clause') {
    //       return true
    //     }
    //     if (filterItem.type === 'group') {
    //       return filterItem.children.some(containsClause)
    //     }
    //     return false
    //   }
    //   return containsClause(rootCondition.value) && !!assetTypeIds.value.length
    // })

    const hasAnyFilters = (): boolean => {
      return (
        conditionCount.value > 0 || assetTypeIds.value.length < _getAllAssetTypes().length || assetIds.value.length > 0
      )
    }

    const hasOnlyAssetTypeFilters = (): boolean => {
      return (
        conditionCount.value === 0 &&
        assetIds.value.length === 0 &&
        assetTypeIds.value.length > 0 &&
        assetTypeIds.value.length < _getAllAssetTypes().length
      )
    }

    const hasAdvancedFilters = computed(() => {
      if (rootCondition.value.type !== 'group' || rootCondition.value.operator !== '$and') {
        return true
      }

      for (const child of rootCondition.value.children) {
        if (child.type !== 'group' || child.operator !== '$or') {
          return true
        }

        const groupProperties: string[] = []

        for (const nestedChild of child.children) {
          if (nestedChild.type !== 'clause' || nestedChild.operator !== '=') {
            return true
          }

          if (!groupProperties.includes(propertyFamily(nestedChild.property))) {
            groupProperties.push(propertyFamily(nestedChild.property))
          }
        }

        if (groupProperties.length > 1) {
          return true
        }
      }

      return false
    })

    const toBasicFilters = computed<BasicFilters | null>(() => {
      if (rootCondition.value.type !== 'group' || rootCondition.value.operator !== '$and') {
        return null
      }

      const basicFilters: BasicFilters = {}

      for (const child of rootCondition.value.children) {
        if (child.type !== 'group' || child.operator !== '$or') {
          return null
        }

        for (const clause of child.children) {
          if (
            clause.type !== 'clause' ||
            clause.operator !== '=' ||
            typeof clause.property !== 'string' ||
            !clause.property
          ) {
            return null
          }

          const filterKey = clause.property

          if (!basicFilters[filterKey]) {
            basicFilters[filterKey] = []
          }

          basicFilters[filterKey].push(clause.value)
        }
      }

      return basicFilters
    })

    // Reset Function
    const $reset = (): void => {
      id.value = 0
      name.value = ''
      groupBy.value = 'MODULE'
      assetTypeIds.value = []
      assetIds.value = []
      shareLevel.value = 'PRIVATE'
      rootCondition.value = {
        _id: 'root',
        type: 'group',
        operator: '$and',
        children: []
      }
      availableAssetTypes.value = []
    }

    return {
      // State
      id,
      name,
      groupBy,
      assetTypeIds,
      assetIds,
      shareLevel,
      rootCondition,
      availableAssetTypes,

      // Getters
      filters,
      conditionCount,
      hasValidFilters,
      hasAdvancedFilters,
      toBasicFilters,
      hasAnyFilters,
      hasOnlyAssetTypeFilters,

      // Actions
      $reset,
      addClause,
      addGroup,
      fromBasicFilters,
      updateClause,
      removeItemById,
      setId,
      setName,
      setGroupBy,
      setAssetTypeIds,
      setAssetIds,
      setShareLevel,
      setRootCondition,
      clearAllConditions,
      resetClauses,
      findClauseInGroup,
      findClausesByPropertyInGroup,
      setAvailableAssetTypes
    }
  },
  {
    persist: {
      storage: piniaPluginPersistedstate.localStorage()
    }
  }
)
